/**
 * ============================================================
 * 「常一起购买」交互逻辑（阿墨自建，2026-07）
 * 大白话说明：
 *  1. 页面加载后先显示"同系列商品"垫底，同时去问 Shopify 官方
 *     "买过这个的人还买了什么"，问到了就把垫底的换成官方推荐
 *  2. 勾选框控制哪些商品参与合计和加购
 *  3. 点按钮 → 一次性把勾中的全部加进购物车 → 自动弹出购物车抽屉
 *  4. 完全独立的自定义元素 <fbt-bundle>，不 import 主题任何模块，
 *     只通过主题约定的 'cart:update' 事件跟购物车抽屉/角标联动
 * ============================================================
 */

/** 复刻主题 CartAddEvent 的事件结构，让抽屉、角标、购物车列表都能听懂 */
class FbtCartUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super('cart:update', { bubbles: true });
    this.detail = { resource, sourceId, data: { ...data } };
  }
}

/** 把"分"格式化成带货币符号的价格，支持 Shopify 常见的几种 money_format 占位符 */
function fbtFormatMoney(cents, format) {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  function addThousands(numStr, sep) {
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }
  function formatWithDelimiters(c, precision, thousands, decimal) {
    const fixed = (c / 100).toFixed(precision);
    const parts = fixed.split('.');
    const whole = addThousands(parts[0], thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }
  let value = '';
  const match = format.match(placeholderRegex);
  const key = match ? match[1] : 'amount';
  switch (key) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2, ',', '.');
  }
  return format.replace(placeholderRegex, value);
}

class FbtBundle extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.addButton = this.querySelector('[data-fbt-add]');
    this.errorEl = this.querySelector('[data-fbt-error]');

    this.addEventListener('change', this.onChange);
    this.addButton?.addEventListener('click', this.onAddToCart);

    // 页面上方主规格选择器变化时，同步"当前商品"这一行的变体和价格
    document.addEventListener('variant:update', this.onMainVariantUpdate);

    this.rebuildGallery();
    this.updateTotal();
    this.fetchOfficialRecommendations();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.onMainVariantUpdate);
  }

  /** 所有商品行 */
  get rows() {
    return Array.from(this.querySelectorAll('[data-fbt-item]'));
  }

  /** 勾中的商品行 */
  get checkedRows() {
    return this.rows.filter((row) => row.querySelector('[data-fbt-check]')?.checked);
  }

  /** 去问 Shopify 官方推荐算法，问到了就替换垫底的同系列商品 */
  async fetchOfficialRecommendations() {
    const { recsUrl, sectionId, productId, maxItems } = this.dataset;
    if (!recsUrl || !sectionId || !productId) return;
    try {
      const url = `${recsUrl}?section_id=${sectionId}&product_id=${productId}&limit=${maxItems || 2}&intent=related`;
      const res = await fetch(url);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const remoteItems = doc.querySelectorAll('[data-fbt-remote-items] [data-fbt-item]');
      if (remoteItems.length === 0) return; // 官方没数据，保留垫底方案

      // 保留第一行（当前商品），后面的行全部换成官方推荐
      const list = this.querySelector('[data-fbt-list]');
      if (!list) return;
      this.rows.forEach((row, i) => {
        if (i > 0) row.remove();
      });
      remoteItems.forEach((item) => {
        // 官方推荐里不会包含当前商品，直接追加
        if (item.dataset.productId !== this.dataset.productId) {
          list.appendChild(item);
        }
      });
      this.rebuildGallery();
      this.updateTotal();
    } catch (e) {
      // 静默失败，垫底方案照常展示
      console.warn('[fbt] recommendations fetch failed', e);
    }
  }

  /** 勾选 / 换规格 → 重新算价 */
  onChange = (event) => {
    const target = event.target;
    if (target.matches('[data-fbt-variant]')) {
      // 换了规格，把这一行的显示价格也换掉
      const row = target.closest('[data-fbt-item]');
      const option = target.selectedOptions?.[0];
      const priceEl = row?.querySelector('[data-fbt-price]');
      if (option && priceEl) {
        priceEl.dataset.cents = option.dataset.price;
        priceEl.textContent = fbtFormatMoney(Number(option.dataset.price), this.moneyFormat);
      }
    }
    if (target.matches('[data-fbt-check]')) {
      this.syncGalleryState();
    }
    this.updateTotal();
  };

  /** 主商品换规格 → 更新第一行的变体 id 和价格 */
  onMainVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant?.id) return;
    const currentInput = this.querySelector('[data-fbt-current-variant]');
    if (!currentInput) return;
    currentInput.value = variant.id;
    if (typeof variant.price === 'number') {
      currentInput.dataset.price = String(variant.price);
      const priceEl = currentInput.closest('[data-fbt-item]')?.querySelector('[data-fbt-price]');
      if (priceEl) {
        priceEl.dataset.cents = String(variant.price);
        priceEl.textContent = fbtFormatMoney(variant.price, this.moneyFormat);
      }
    }
    this.updateTotal();
  };

  /** 顶部图片横排：从每行商品图克隆出来，中间加 + 号 */
  rebuildGallery() {
    const gallery = this.querySelector('[data-fbt-gallery]');
    if (!gallery) return;
    gallery.innerHTML = '';
    this.rows.forEach((row, i) => {
      const img = row.querySelector('.fbt-item__img');
      if (!img) return;
      if (i > 0) {
        const plus = document.createElement('span');
        plus.className = 'fbt__gallery-plus';
        plus.textContent = '+';
        gallery.appendChild(plus);
      }
      const clone = img.cloneNode(true);
      clone.className = 'fbt__gallery-thumb';
      clone.removeAttribute('sizes');
      clone.dataset.fbtGalleryFor = String(i);
      gallery.appendChild(clone);
    });
    this.syncGalleryState();
  }

  /** 取消勾选的商品，顶部对应的图片变淡 */
  syncGalleryState() {
    const gallery = this.querySelector('[data-fbt-gallery]');
    if (!gallery) return;
    this.rows.forEach((row, i) => {
      const checked = row.querySelector('[data-fbt-check]')?.checked;
      const thumb = gallery.querySelector(`[data-fbt-gallery-for="${i}"]`);
      thumb?.classList.toggle('fbt__gallery-thumb--off', !checked);
    });
  }

  /**
   * 解析阶梯折扣配置，比如 "3:10,5:15" = 满3件9折、满5件85折
   * 返回按件数从小到大排好的 [{qty, pct}, ...]
   */
  get discountTiers() {
    return (this.dataset.discountTiers || '')
      .split(',')
      .map((s) => {
        const [qty, pct] = s.split(':').map(Number);
        return { qty, pct };
      })
      .filter((t) => t.qty > 0 && t.pct > 0)
      .sort((a, b) => a.qty - b.qty);
  }

  /**
   * 合计价格 = 勾中行的价格之和，支持阶梯折扣（如满3件10%、满5件15%）：
   *  - 按勾选件数命中最高的一档 → 原价划线 + 折后价 + "You save $X (15%)"
   *  - 离更高一档只差 1~2 件 → 显示"再加 N 件立省 X%"勾引往上凑
   *    （已享受低档折扣时也会提示更高档，两行同时显示）
   * 注意：这里只是前端显示，真正扣钱靠后台建的自动折扣，档位要一致
   */
  updateTotal() {
    const totalEl = this.querySelector('[data-fbt-total]');
    if (!totalEl) return;
    const cents = this.checkedRows.reduce((sum, row) => {
      const priceEl = row.querySelector('[data-fbt-price]');
      return sum + (Number(priceEl?.dataset.cents) || 0);
    }, 0);

    const checkedCount = this.checkedRows.length;
    const tiers = this.discountTiers;
    // 命中的档 = 件数够得着的最高一档；下一档 = 还差几件的最近一档
    let hit = null;
    let next = null;
    for (const t of tiers) {
      if (checkedCount >= t.qty) hit = t;
      else if (!next) next = t;
    }

    const originalEl = this.querySelector('[data-fbt-total-original]');
    const savingsEl = this.querySelector('[data-fbt-savings]');
    const nudgeEl = this.querySelector('[data-fbt-nudge]');

    if (hit) {
      const discounted = Math.round((cents * (100 - hit.pct)) / 100);
      const saved = cents - discounted;
      totalEl.textContent = fbtFormatMoney(discounted, this.moneyFormat);
      if (originalEl) {
        originalEl.textContent = fbtFormatMoney(cents, this.moneyFormat);
        originalEl.hidden = false;
      }
      if (savingsEl) {
        savingsEl.textContent = `${savingsEl.dataset.savingsLabel || 'You save'} ${fbtFormatMoney(saved, this.moneyFormat)} (${hit.pct}%)`;
        savingsEl.hidden = false;
      }
    } else {
      totalEl.textContent = fbtFormatMoney(cents, this.moneyFormat);
      if (originalEl) originalEl.hidden = true;
      if (savingsEl) savingsEl.hidden = true;
    }

    if (nudgeEl) {
      // 离下一档只差 1~2 件才提示，一件没勾就不聒噪
      const missing = next ? next.qty - checkedCount : 0;
      const showNudge = !!next && checkedCount > 0 && missing > 0 && missing <= 2;
      if (showNudge) {
        nudgeEl.textContent = (nudgeEl.dataset.nudgeText || 'Add [count] more to save [percent]%')
          .replace('[count]', String(missing))
          .replace('[percent]', String(next.pct));
      }
      nudgeEl.hidden = !showNudge;
    }

    // 一件都没勾就禁用按钮
    if (this.addButton) this.addButton.disabled = checkedCount === 0;
  }

  /** 一键把勾中的全部加进购物车 */
  onAddToCart = async () => {
    const items = this.checkedRows
      .map((row) => {
        const variantEl = row.querySelector('[data-fbt-variant]');
        const id = Number(variantEl?.value);
        return id ? { id, quantity: 1 } : null;
      })
      .filter(Boolean);
    if (items.length === 0) return;

    this.setError('');
    const btn = this.addButton;
    const label = btn?.querySelector('[data-fbt-add-label]');
    const originalText = label?.textContent;
    if (btn) btn.disabled = true;
    if (label) label.textContent = '...';

    try {
      // 把购物车列表组件的 section id 一起带上，返回的 HTML 直接喂给主题去刷新抽屉
      const cartSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      const res = await fetch(this.dataset.addUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: cartSectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }

      // 拿最新购物车总数，喂给角标
      let cart = null;
      try {
        const cartRes = await fetch(`${this.dataset.cartUrl || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (cartRes.ok) cart = await cartRes.json();
      } catch (e) { /* 拿不到就算了，不影响主流程 */ }

      // 广播主题标准的 cart:update 事件：抽屉自动弹出、角标更新、列表刷新
      this.dispatchEvent(
        new FbtCartUpdateEvent(cart ?? undefined, this.dataset.sectionId || 'fbt', {
          source: 'fbt-bundle-component',
          itemCount: cart?.item_count ?? undefined,
          sections: data.sections || undefined,
        })
      );

      if (label) label.textContent = '✓';
      setTimeout(() => {
        if (label && originalText) label.textContent = originalText;
      }, 2000);
    } catch (err) {
      this.setError(err.message || 'Something went wrong. Please try again.');
      if (label && originalText) label.textContent = originalText;
    } finally {
      if (btn) btn.disabled = false;
      this.updateTotal();
    }
  };

  setError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = !msg;
  }
}

if (!customElements.get('fbt-bundle')) {
  customElements.define('fbt-bundle', FbtBundle);
}
